// Imports ------------------------------------------------------------------->

import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";

import {Page, renderFooter} from "../flipp-page/Page";
import {Link} from "../flipp-link/Link";
import {FlyerListingItem, FlyerListingItemContext} from "../flipp-flyer-listing-item/FlyerListingItem";
import {LoadingSpinner} from "../flipp-loading/LoadingSpinner";
import {AppRating} from "../flipp-app-rating/AppRating";
import {BlackFridayBanner} from "../flipp-blackfriday-banner/BlackFridayBanner";

import {inject} from "../../services/di";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {StorageService} from "../../services/StorageService";
import {ConfigService} from "../../services/ConfigService";
import {FlyerPlacement} from "../../services/definitions/IAnalyticsService";
import {UserAgentService} from "../../services/UserAgentService";
import FlyersSortService from "../../services/FlyersSortService";
import { SeoService } from "../../services/SeoService";
import FeedbackService from "../../services/FeedbackService";
import {BlackFridayService} from "../../services/BlackFridayService";
import { BannerService } from "../../services/BannerService";
import {
	BrandMediaService,
	FLYER_LIST_AD_SLOT_ID,
	FLYER_LIST_AD_SLOT_POSITION_INDEX
} from "../../services/BrandMediaService";
import SlotResponseReceived = googletag.events.SlotResponseReceived;

const AD_SLOT_RESPONSE_TIMEOUT = 1000;

interface ListingPageAttributes {
	category?: string;
	withSidebar?: boolean;
	sort?: string;
}

@element("flipp-listing-page")
export class ListingPage extends Component<ListingPageAttributes, Flyer[]> {
	@attr category: string = "All Flyers";
	@attr sort: string = "featured";
	@attr withSidebar: boolean = true;

	refs: {
		title: HTMLElement,
		content: HTMLElement,
		page: HTMLElement
	};

	state = {
		scrollTop: 0
	};

	render() {
		return <Page ref="page" show="flyers" onScroll={this._handleScroll}>
			<ListingPageContent sort={this.sort} category={this.category} />
		</Page>;
	}

	onRender() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	onAttach() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	private _handleScroll = (e: Event) => {
		this.state.scrollTop = this.refs.page.scrollTop;
	}

}

// --- flipp-listing-page-content --->

interface ListingPageContentAttributes {
	category?: string;
	sort?: string;
	shouldDivideFlyers?: boolean;
}

const FLYER_LIST_AD_SLOT_CONTAINER_ID = "ad-slot";

@element("flipp-listing-page-content")
export class ListingPageContent extends Component<ListingPageContentAttributes, Flyer[]> {

	@inject location: LocationService;
	@inject flyerService: FlyerService;
	@inject analytics: AnalyticsService;
	@inject storage: StorageService;
	@inject config: ConfigService;
	@inject userAgent: UserAgentService;
	@inject flyersSortService: FlyersSortService;
	@inject seoService: SeoService;
	@inject feedbackService: FeedbackService;
	@inject blackFridayService: BlackFridayService;
	@inject bannerService: BannerService;
	@inject bm: BrandMediaService;

	@attr category: string = "All Flyers";
	@attr sort: string = "featured";
	@attr shouldDivideFlyers: boolean = false;

	isAdInited = true;
	slotResponse: any = null;
	displayAdTimeoutId: any = null;

	prepare() {
		return this.flyerService.getFlyers();
	}

	onAttributeChange(name: string, oldVal: string, newVal: string) {
		if (name === "sort" && this.bm.shouldDisplayAd()) {
			this.isAdInited = false;
		}
		super.onAttributeChange(name, oldVal, newVal);
	}

	onRender() {
		if (!this.isAdInited && !this.userAgent.mobile) {
			try {
				this.initAds();
			} catch (e) {
				this.bm.disableAd();
				this.invalidate();
				console.log(e);
			}
		}
	}

	onDetach() {
		clearTimeout(this.displayAdTimeoutId);
	}

	initAds() {
		googletag.cmd.push(() => {
			const slot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === FLYER_LIST_AD_SLOT_ID);
			googletag.pubads().refresh([slot]);
			googletag.pubads().addEventListener("slotResponseReceived", (event) => {
				this.slotResponse = event.slot.getResponseInformation();
				if (this.slotResponse) {
					this.hideAdSlotSpinner();
					const slotElementId = event.slot.getSlotElementId();
					Cookies.set("brandMedia", JSON.stringify(this.slotResponse), {domain: ".flipp.com"});
					// Display ad slot content
					if (slotElementId === FLYER_LIST_AD_SLOT_ID) {
						const slotContainer = document.getElementById(FLYER_LIST_AD_SLOT_ID);
						if (slotContainer) {
							slotContainer.className = "ad-slot-content show";
						}
					}
				} else if (this.bm.shouldDisplayAd()) {
					this.bm.disableAd();
					const el = document.getElementById(FLYER_LIST_AD_SLOT_CONTAINER_ID);
					el.style.display = "none";
				}
			});
			googletag.pubads().addEventListener("impressionViewable", () => {
				this.hideAdSlotSpinner();
			});
		});
		// Hide empty ad slot if no response from ad network
		this.displayAdTimeoutId = setTimeout(() => {
			if (!this.slotResponse && this.bm.shouldDisplayAd() && this.sort === "featured") {
				this.bm.disableAd();
				this.invalidate();
			}
		}, AD_SLOT_RESPONSE_TIMEOUT);
		this.isAdInited = true;
	}

	hideAdSlotSpinner() {
		const spinnerEl = document.getElementsByClassName("spinner-wrapper show");
		// Hide spinner
		if (spinnerEl.length) {
			spinnerEl[0].className = "spinner-wrapper";
		}
	}

	renderLoading() {
		return <LoadingSpinner />;
	}

	renderPremiumFlyer(flyer: Flyer, index: number) {
		return <span class="premium-flyer-wrapper">
					<FlyerListingItem   flyerId={flyer.id}
									context={FlyerListingItemContext.Listing}
									premium
									category={this.category}
									onClick={this._handleFlyerClick.bind(this, index, FlyerPlacement.Promoted)} />
					{index === 2 || index > 4 && (index + 1) % 3 === 0 ? this.renderFeedbackCard() : null}
				</span>;
	}

	renderAd() {
		return <span id={FLYER_LIST_AD_SLOT_CONTAINER_ID} class="premium-flyer-wrapper">
					<div class="ad-slot-wrapper">
						<div id={FLYER_LIST_AD_SLOT_ID} class="ad-slot-content"/>
					</div>
				</span>;
	}

	render(flyers: Flyer[]) {
		let { premiumFlyers, normalFlyers } =
				this.flyersSortService.filter(flyers, this.category, this.sort);

		if (this.isBlogSortOnHost()) {
			premiumFlyers = [];
			normalFlyers = [];
		}
		// Adding null - hack to render ad slot
		let premiumFlyersContent = premiumFlyers;
		if (this.bm.shouldDisplayAd() && this.sort === "featured") {
			if (premiumFlyers.length > 0) {
				premiumFlyersContent = [...premiumFlyers.slice(0, FLYER_LIST_AD_SLOT_POSITION_INDEX), null, ...premiumFlyers.slice(FLYER_LIST_AD_SLOT_POSITION_INDEX, premiumFlyers.length)];
			} else if (!this.blackFridayService.isOnHost()) {
				// Display only ad slot if no premium flyers
				premiumFlyersContent = [null];
			}
		}
		const showDiscoverSlModal = !this._wasDiscoverSlModalClosed() && !this.userAgent.mobile;
		const discoverSL = (
			<Link class="discover-sl-wrapper" to="shopping_list" onClick={this._persistHidingDiscoverSlModal}>
				<div class="discover-sl" flex-horizontal>
					<button class="close" onClick={this._closeDiscoverSlModal}>{icons.x}</button>
					<div flex-horizontal>
						<div>
							<img class="myDiscoverSl" src="https://f.wishabi.net/creative/Distribution/images/icons/myDiscoverSl.svg" alt=""/>
						</div>
						<div class="discover-msg">
							<p>{i18n("shopping_list_cta")}</p>
						</div>
						<div>
							<img class="myArrowUp" src="https://f.wishabi.net/creative/Distribution/images/icons/myArrowUp.svg" alt=""/>
						</div>
					</div>
				</div>
			</Link>);

		this.seoService.insertLoadingMarker(70);

		return <div>
				<div class="topbar">
					<div class="sort-tabs"
							hidden={premiumFlyers.length === 0 && normalFlyers.length === 0}
							onClick={this._handleSort}>
						<button title={i18n("featured_sort_help")}
										aria-label={i18n("featured_sort_help")}
										sort="featured">{i18n("featured")}</button>
						<button title={i18n("latest_sort_help")}
										aria-label={i18n("latest_sort_help")}
										sort="latest">{i18n("latest")}</button>
						<button title={i18n("alphabetical_sort_help")}
										aria-label={i18n("alphabetical_sort_help")}
										sort="alphabetical">{i18n("alphabetical")}</button>
						{/*{this.blackFridayService.isOnHost() && this.blackFridayService.shouldRenderBlogSection() ? <button sort="blog">{i18n("blog_button")}</button> : null}*/}
					</div>

					<h1 class="title">{this.getH1()}</h1>
					{showDiscoverSlModal ? discoverSL : null}
				</div>
				<div class="content">
					{this.blackFridayService.isEnabled() ? <BlackFridayBanner /> : null}
					{ premiumFlyersContent.map((flyer, index) => {
							if (flyer) {
								return this.renderPremiumFlyer(flyer, index);
							} else if (!this.userAgent.mobile) {
								return this.renderAd();
							}
						}
					)}
					{this.insertDividerAfterPremiumFlyers()}
					{ normalFlyers.map((flyer, idx) =>
							<FlyerListingItem flyerId={flyer.id} context={FlyerListingItemContext.Listing}
								category={this.category}
								onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Organic)} />) }

					{ premiumFlyers.length === 0 && normalFlyers.length === 0 ?
						<div class="empty-item-container">
							<div>{this.renderNoFlyersMessage()}</div>
						</div>
					: null }
				</div>
				{!this.blackFridayService.isOnHost() ? renderFooter(this.location.locale) : null}
			</div>;
	}

	renderNoFlyersMessage() {
		const isBlackFriday = document.location.href.includes("black-friday");
		if (isBlackFriday) {
			if (this.isBlogSortOnHost()) return null;
			return i18n("no_flyers_for_blackfriday_page");
		} else {
			return i18n("no_flyers_for_postal_code", {
				postalCode: this.location.postalCode
			});
		}
	}


	insertDividerAfterPremiumFlyers() {
		return this.shouldDivideFlyers ? (<div/>) : null;
	}

	private renderFeedbackCard() {
		if (this.bannerService.isSmartNews() || this.config.shouldDisableMobileBanners() || !this.userAgent.mobile) {
			return;
		}
		const buttonClass = this.location.locale === "fr-ca" ? "french" : "";
		return <div class="feedback-card">
			<div class="feedback-image">
				<img src="https://f.wishabi.net/creative/Distribution/images/icons/feedback-img.png" alt="feedback-image"/>
			</div>
			<div class="feedback-text-wrapper">
				<div class="feedback-text">{this.feedbackService.getFeedback()}</div>
				<div class="feedback-rating">
					<AppRating />
				</div>
			</div>
			<div class="feedback-card-button">
				<a href="https://click.flipp.com/h8pE/1c6a8a4d">
					<button class={buttonClass}>{i18n("get_app")}</button>
				</a>
			</div>
		</div>;
	}

	private getH1 = () => {
		let title = "flyers_for_postal_code_category";
		if (this.location.city && this.location.city !== "undefined" && this.location.city !== "null") {
			title = "flyers_for_city_postal_code_category";
			if (this.category !== "All Flyers") {
				title = "category_flyers_for_city_postal_code_category";
			} else {
				title = "index_flyers_h1";
			}
		}

		return i18n(title, {
			postalCode: this.location.postalCode,
			city: this.location.city,
			category: i18n(this.category)
		});
	}

	private isBlogSortOnHost = () => this.blackFridayService.isOnHost() && this.sort === "blog";

	private _closeDiscoverSlModal = (e: Event) => {
		if (e) {
			// If clicked on the close X, then prevent the <Link to=sl /> from kicking into gear.
			// ----------------------------------------------------------------------
			e.preventDefault();
		}

		this.storage.setItem("discover_sl_modal_closed", JSON.stringify({"closed": true}));
		this.invalidate();
	}

	private _persistHidingDiscoverSlModal = (e: Event) => {
		this.storage.setItem("discover_sl_modal_closed", JSON.stringify({"closed": true}));
	}

	private _wasDiscoverSlModalClosed = () => {
		const value = this.storage.getItem("discover_sl_modal_closed");
		if (value) {
			const data = JSON.parse(value);
			return data.closed;
		}
		return false;
	}

	private _handleFlyerClick = (idx: number, placement: FlyerPlacement, e: Event) => {
		this.analytics.trackFlyerClick(this.category, this.sort, placement, idx);
	}

	private _handleSort = (e: Event) => {
		const target = e.target;
		if (target instanceof HTMLButtonElement &&
				target.hasAttribute("sort")) {
			this.sort = target.getAttribute("sort");

			this.flyerService.getFlyers().then((flyers) => {
				this.analytics.trackFlyerListing(this.category, flyers, this.sort);
			});
		}
	}
}
